import React, { useState, useEffect } from 'react';
import { 
  CalendarClock, Send, Save, Users, Clock, Mail, 
  AlertTriangle, CheckCircle, RefreshCw, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi } from '@/lib/api';

const DAYS_OF_WEEK = [
  { value: '0', label: 'Segunda-feira' },
  { value: '1', label: 'Terça-feira' },
  { value: '2', label: 'Quarta-feira' },
  { value: '3', label: 'Quinta-feira' },
  { value: '4', label: 'Sexta-feira' },
  { value: '5', label: 'Sábado' },
  { value: '6', label: 'Domingo' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${String(i).padStart(2, '0')}:00`
}));

const ROLE_LABELS = {
  'company_admin': 'Administrador',
  'compliance_officer': 'Compliance Officer',
  'triage_manager': 'Gestor de Triagem',
  'dpo': 'DPO'
};

const ScheduledReportsPage = () => {
  const [config, setConfig] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendResult, setSendResult] = useState(null);

  const [formData, setFormData] = useState({
    is_active: false,
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    hour: 8,
    include_triage_manager: true,
    include_compliance_officer: true,
    include_admin: true,
    include_dpo: false
  });

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getScheduledReportConfig();
      setConfig(response.data.config);
      setRecipients(response.data.recipients || []);
      setFormData({
        is_active: response.data.config.is_active || false,
        frequency: response.data.config.frequency || 'weekly',
        day_of_week: response.data.config.day_of_week ?? 1,
        day_of_month: response.data.config.day_of_month ?? 1,
        hour: response.data.config.hour ?? 8,
        include_triage_manager: response.data.config.include_triage_manager ?? true,
        include_compliance_officer: response.data.config.include_compliance_officer ?? true,
        include_admin: response.data.config.include_admin ?? true,
        include_dpo: response.data.config.include_dpo ?? false
      });
    } catch (err) {
      setError('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await adminApi.updateScheduledReportConfig(formData);
      setSuccess('Configuração guardada com sucesso');
      fetchConfig(); // Refresh to get updated recipients
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    setError('');
    setSuccess('');
    setSendResult(null);
    
    try {
      const response = await adminApi.sendScheduledReportNow();
      setSendResult(response.data);
      setSuccess(`Relatório enviado para ${response.data.sent_count} destinatário(s)`);
      fetchConfig(); // Refresh last_sent_at
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar relatório');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-100 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scheduled-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-slate-700" />
            Relatórios Agendados
          </h1>
          <p className="text-slate-600 mt-1">
            Configure o envio automático de relatórios por email
          </p>
        </div>
        <Badge variant={formData.is_active ? "default" : "secondary"} className="text-sm">
          {formData.is_active ? "Ativo" : "Inativo"}
        </Badge>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Send Result */}
      {sendResult && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <h4 className="font-semibold text-blue-900 mb-2">Resultado do Envio</h4>
            <div className="grid md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-600">Novas Denúncias:</span>
                <span className="ml-2 font-bold">{sendResult.stats?.new_reports || 0}</span>
              </div>
              <div>
                <span className="text-blue-600">Resolvidas:</span>
                <span className="ml-2 font-bold">{sendResult.stats?.resolved_reports || 0}</span>
              </div>
              <div>
                <span className="text-blue-600">Em Atraso:</span>
                <span className="ml-2 font-bold text-red-600">{sendResult.stats?.overdue_reports || 0}</span>
              </div>
              <div>
                <span className="text-blue-600">Conflitos:</span>
                <span className="ml-2 font-bold">{sendResult.stats?.conflicts_detected || 0}</span>
              </div>
            </div>
            {sendResult.failed?.length > 0 && (
              <p className="text-red-600 mt-2 text-sm">
                Falha no envio para: {sendResult.failed.join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle>Configuração de Envio</CardTitle>
          <CardDescription>
            Defina a frequência e os destinatários dos relatórios automáticos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Activation Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Relatórios Automáticos Ativos</Label>
              <p className="text-sm text-slate-500">
                Quando ativo, os relatórios serão enviados automaticamente
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              data-testid="reports-active-switch"
            />
          </div>

          <Separator />

          {/* Frequency Settings */}
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Frequência
              </Label>
              <Select 
                value={formData.frequency} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, frequency: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Diário</SelectItem>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.frequency === 'weekly' && (
              <div>
                <Label>Dia da Semana</Label>
                <Select 
                  value={String(formData.day_of_week)} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, day_of_week: parseInt(v) }))}
                >
                  <SelectTrigger className="mt-1" data-testid="day-of-week-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map(day => (
                      <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.frequency === 'monthly' && (
              <div>
                <Label>Dia do Mês</Label>
                <Select 
                  value={String(formData.day_of_month)} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, day_of_month: parseInt(v) }))}
                >
                  <SelectTrigger className="mt-1" data-testid="day-of-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Hora de Envio
              </Label>
              <Select 
                value={String(formData.hour)} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, hour: parseInt(v) }))}
              >
                <SelectTrigger className="mt-1" data-testid="hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map(hour => (
                    <SelectItem key={hour.value} value={hour.value}>{hour.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Recipients */}
          <div>
            <Label className="text-base font-medium flex items-center gap-2 mb-4">
              <Users className="w-4 h-4" />
              Destinatários
            </Label>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Administradores</p>
                  <p className="text-xs text-slate-500">company_admin</p>
                </div>
                <Switch
                  checked={formData.include_admin}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_admin: checked }))}
                  data-testid="include-admin-switch"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Compliance Officers</p>
                  <p className="text-xs text-slate-500">compliance_officer</p>
                </div>
                <Switch
                  checked={formData.include_compliance_officer}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_compliance_officer: checked }))}
                  data-testid="include-compliance-switch"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Gestores de Triagem</p>
                  <p className="text-xs text-slate-500">triage_manager</p>
                </div>
                <Switch
                  checked={formData.include_triage_manager}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_triage_manager: checked }))}
                  data-testid="include-triage-switch"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">DPO</p>
                  <p className="text-xs text-slate-500">Data Protection Officer</p>
                </div>
                <Switch
                  checked={formData.include_dpo}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_dpo: checked }))}
                  data-testid="include-dpo-switch"
                />
              </div>
            </div>

            {/* Recipients Preview */}
            {recipients.length > 0 && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Destinatários Atuais ({recipients.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {recipients.map((r, i) => (
                    <Badge key={i} variant="outline" className="font-normal">
                      {r.name} ({ROLE_LABELS[r.role] || r.role})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Last Sent Info */}
          {config?.last_sent_at && (
            <Alert className="border-slate-200 bg-slate-50">
              <Clock className="w-4 h-4 text-slate-600" />
              <AlertDescription className="text-slate-700">
                Último envio: {new Date(config.last_sent_at).toLocaleString('pt-PT')}
              </AlertDescription>
            </Alert>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-scheduled-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'A guardar...' : 'Guardar Configuração'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manual Send */}
      <Card>
        <CardHeader>
          <CardTitle>Envio Manual</CardTitle>
          <CardDescription>
            Envie um relatório imediatamente para os destinatários configurados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Enviar Relatório Agora</p>
              <p className="text-sm text-slate-500">
                Envia o relatório semanal para todos os destinatários configurados
              </p>
            </div>
            <Button 
              onClick={handleSendNow} 
              disabled={sending || recipients.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-now-btn"
            >
              {sending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {sending ? 'A enviar...' : 'Enviar Agora'}
            </Button>
          </div>
          
          {recipients.length === 0 && (
            <p className="text-sm text-amber-600 mt-2">
              Configure pelo menos um perfil de destinatário para poder enviar relatórios.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduledReportsPage;
